import { NgTemplateOutlet } from '@angular/common';
import { Component, DoCheck, EventEmitter, Input, IterableDiffer, IterableDiffers, OnDestroy, Output, ViewChildren } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { ListViewComponent } from 'components/layout/list-view/list-view.component';
import { InputType } from 'enums/InputType';
import { LegalCaseStatus } from 'enums/LegalCaseStatus';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { LegalCaseModel } from 'models/generated/Entities.GormCrm.LegalCase';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { SettlementModel } from 'models/generated/Entities.GormCrm.Settlement';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Pipes } from 'modules/pipes';
import { Subscription } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { ProductPlanService } from 'services/productplan.service';

@Component({
	selector: 'company-products-list',
	templateUrl: './company-products-list.component.html',
	imports: [NgTemplateOutlet, ListView, Layout, CustomInputComponent, Pipes]
})
export class CompanyProductsListComponent implements DoCheck, OnDestroy {

	@ViewChildren(ListViewComponent) listViews!: ListViewComponent[];

	public ready = false;

	private originalDiffer?: IterableDiffer<ProductPlanModel>;
	private dataProvider: CompanyDataProvider;

	private allLedgerPosts: LedgerPostModel[] = [];
	private _productPlans?: ProductPlanModel[];
	private _rootPlans?: ProductPlanModel[];
	public inputTypeEnum = InputType;
	public activeAccountingPlans = new Map<ProductPlanModel, AccountingPlanModel>();
	@Input() set productPlans(plans: ProductPlanModel[] | undefined) {
		this._productPlans = plans;
		this.init();
	}
	get productPlans() { return this._productPlans; }
	get rootPlans() { return this._rootPlans }

	@Input('multiSelect') multiSelectFilter?: (plan: ProductPlanModel) => boolean;
	@Input() settlement?: SettlementModel;
	@Input() legalCases?: LegalCaseModel[];

	@Output() selectedProductPlanChange = new EventEmitter<ProductPlanModel>();
	@Input() selectedProductPlan?: ProductPlanModel;

	@Output() selectedProductPlansChange = new EventEmitter<ProductPlanModel[]>();
	@Input() selectedProductPlans: ProductPlanModel[] = [];

	@Input() sortColumns = true;

	@Input() showCurrentAccountingPlanData: boolean = false;

	private _onlyActivePlans: boolean = false;
	@Input() set onlyActivePlans(value: boolean) {
		this._onlyActivePlans = value;
		if (value) this.showInactivePlans = false;
	}
	get onlyActivePlans() { return this._onlyActivePlans; }

	showInactivePlans: boolean = false;
	hasInactivePlans: boolean = false;

	async init() {
		if (this._productPlans) this.allLedgerPosts = await this.dataProvider.getLedgerPostsByProductPlanIds(this._productPlans.map(p => p.id!));
		this.originalDiffer = this.iterableDiffers.find(this._productPlans).create<ProductPlanModel>();
	}

	ngDoCheck() {
		if (!this._productPlans || !this.originalDiffer) return;
		const originalChanges = this.originalDiffer.diff(this._productPlans);
		if (originalChanges) {
			if (!this._rootPlans) this._rootPlans = [];
			this._rootPlans.length = 0;
			this._rootPlans.push(...this.productPlans!.filter(plan => !plan.parent));
			this.productPlans?.forEach(plan => {
				this.activeAccountingPlans.set(plan, this.showCurrentAccountingPlanData ? this.accountingService.filterCurrentAccountingPlan(plan.accountingPlans) : this.accountingService.filterActiveAccountingPlan(plan.accountingPlans));
			});
			this.setHasInactivePlans();
		}
		if (!this.ready) setTimeout(() => { this.ready = true; });
	}

	planIsInSettlement(productPlanId: string): boolean {
		return this.settlement != undefined && this.settlement.settlementProductPlans.find(spp => spp.productPlanId == productPlanId) != undefined;
	}

	planIsInDebtCollection(productPlanId: string): boolean {
		return this.legalCases?.filter(lc => lc.status != LegalCaseStatus.Closed).find(lc => lc.legalCaseProductPlans.some(lcpp => lcpp.productPlanId == productPlanId)) != undefined;
	}

	statusColor(plan: ProductPlanModel): string {
		if (this.planIsInSettlement(plan.id!)) return 'color-orange-40';
		if (this.planIsInDebtCollection(plan.id!)) return 'color-red-70';
		return this.accountingService.statusColor(plan, this.activeAccountingPlans.get(plan))
	}

	statusText(plan: ProductPlanModel): string {
		if (this.planIsInSettlement(plan.id!)) return 'company.product-plans.status.in-settlement';
		if (this.planIsInDebtCollection(plan.id!)) return 'company.product-plans.status.in-debt-collection';
		return this.accountingService.statusTitle(plan, this.activeAccountingPlans.get(plan));
	}

	selectProductPlan(plan: ProductPlanModel) {
		if (this.multiSelectFilter) {
			if (!this.multiSelectFilter(plan)) return;
			const currentIndex = this.selectedProductPlans.indexOf(plan);
			if (currentIndex >= 0)
				this.selectedProductPlans.splice(currentIndex, 1);
			else
				this.selectedProductPlans.push(plan);
			this.selectedProductPlansChange.emit(this.selectedProductPlans);
		}
		else {
			if (this.selectedProductPlan === plan)
				this.selectedProductPlan = undefined;
			else
				this.selectedProductPlan = plan;
			this.selectedProductPlanChange.emit(this.selectedProductPlan);
		}
	}

	getAnnualContractValue(plan: ProductPlanModel): number | undefined {
		const accountingPlan = this.activeAccountingPlans.get(plan);
		return this.accountingService.generateAnnualContractValue(accountingPlan?.startDate, accountingPlan?.endDate, accountingPlan?.totalPlanValue);
	}

	showPlan(productPlan: ProductPlanModel, rootLevel: boolean = false) {
		return this.productPlanService.showPlan(productPlan, this.activeAccountingPlans, this.allLedgerPosts, this.showInactivePlans, rootLevel);
	}

	setHasInactivePlans() {
		this.productPlans?.forEach(plan => {
			plan.show = this.showPlan(plan, !plan.parent);
		});
		this.hasInactivePlans = this.productPlans?.some(plan => !plan.show) ?? false;
	}

	private onBillingPlanChanges: Subscription;

	constructor(dataProviderFactory: CompanyDataProviderFactory,
		public readonly accountingService: AccountingService,
		private readonly productPlanService: ProductPlanService,
		private iterableDiffers: IterableDiffers) {
		this.dataProvider = dataProviderFactory.getProviderForCurrentScope();

		this.onBillingPlanChanges = this.dataProvider.onBillingPlanChanges(async data => {

			if (!this.productPlans) return;
			const productPlanIds = this.productPlans.map(p => p.id!);
			if (!productPlanIds.includes(data.productPlanId)) return;

			this.allLedgerPosts = await this.dataProvider.getLedgerPostsByProductPlanIds(this.productPlans.map(p => p.id!));
			this.setHasInactivePlans();
		});
	}

	ngOnDestroy() {
		this.onBillingPlanChanges.unsubscribe();
	}
}
